import React from "react";

import { separateInputWrapperPropsAndInputProps } from "../../../../../organisms/Form/utils";

import { InputText, InputTextProps } from "../../../../../atoms/InputText";
import { usePyForm } from "../../../context";
import { BaseFormFieldProps, FormField } from "../../FormComponents/FormField";

// Get all unique props from InputText that aren't in BaseFormFieldProps
type UniqueInputProps = Omit<InputTextProps, keyof BaseFormFieldProps<string> | "value" | "onChange" | "onBlur">;

export type FormInputTextProps = Omit<BaseFormFieldProps<string>, "input"> & UniqueInputProps;

/**
 * This is a form component that is used to create an input text field.
 * It is a wrapper around the InputText component.
 * It is used to create an input text field that is connected to the PyForm context.
 * Its props cross-cut with the InputText component and the FormField component.
 *
 * InputText is an Isotope of InputBase with additional props for text input.
 * It is a controlled component that uses the value and onChange props to update the input value.
 */
export const FormInputText = (props: FormInputTextProps) => {
	const form = usePyForm();
	const { inputWrapperProps, inputProps } = separateInputWrapperPropsAndInputProps(props, form.id);

	if (!form) {
		throw new Error("FormInputText must be used within a Form component");
	}

	return (
		<FormField<string>
			{...inputWrapperProps}
			input={(field) => {
				return (
					<InputText
						{...inputProps}
						value={field.value}
						onChange={field.onChange}
						onBlur={field.onBlur}
						id={inputWrapperProps.id}
						data-testid={`${inputWrapperProps["data-testid"]}-input-text`}
						disabled={inputWrapperProps.disabled}
					/>
				);
			}}
		/>
	);
};

export default FormInputText;
